import { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Wrench, Activity, Shield, Cog, Droplet } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const AddMaintenanceModal = ({ isOpen, onClose, vehicle, onMaintenanceAdded }) => {
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  // Form state
  const [formData, setFormData] = useState({
    technicianId: '',
    generalNotes: '',
    currentMileage: 0,
  });

  // Component data state
  const [componentData, setComponentData] = useState({
    suspension: {
      health: 100,
      maintenanceDate: '',
      mileageSinceService: 0,
      conditionNotes: '',
    },
    brakes: {
      health: 100,
      maintenanceDate: '',
      mileageSinceService: 0,
      padThickness: '',
      fluidLevel: 'Normal',
      conditionNotes: '',
    },
    engine: {
      health: 100,
      maintenanceDate: '',
      mileageSinceService: 0,
      oilQuality: 'Good',
      conditionNotes: '',
    },
    transmission: {
      health: 100,
      maintenanceDate: '',
      mileageSinceService: 0,
      shiftQuality: 'Smooth',
      conditionNotes: '',
    },
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Options for dropdowns
  const oilQualityOptions = ['Excellent', 'Good', 'Needs Change'];
  const shiftQualityOptions = ['Smooth', 'Acceptable', 'Rough'];
  const fluidLevelOptions = ['Low', 'Normal', 'Full'];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        technicianId: '',
        generalNotes: '',
        currentMileage: vehicle?.mileage || 0,
      });
      setComponentData({
        suspension: {
          health: typeof vehicle?.components?.suspension === 'object' 
            ? vehicle.components.suspension.health 
            : (vehicle?.components?.suspension || 100),
          maintenanceDate: '',
          mileageSinceService: 0,
          conditionNotes: '',
        },
        brakes: {
          health: typeof vehicle?.components?.brakes === 'object'
            ? vehicle.components.brakes.health
            : (vehicle?.components?.brakes || 100),
          maintenanceDate: '',
          mileageSinceService: 0,
          padThickness: '',
          fluidLevel: 'Normal',
          conditionNotes: '',
        },
        engine: {
          health: typeof vehicle?.components?.engine === 'object'
            ? vehicle.components.engine.health
            : (vehicle?.components?.engine || 100),
          maintenanceDate: '',
          mileageSinceService: 0,
          oilQuality: 'Good',
          conditionNotes: '',
        },
        transmission: {
          health: typeof vehicle?.components?.transmission === 'object'
            ? vehicle.components.transmission.health
            : (vehicle?.components?.transmission || 100),
          maintenanceDate: '',
          mileageSinceService: 0,
          shiftQuality: 'Smooth',
          conditionNotes: '',
        },
      });
      setError('');
    }
  }, [isOpen, vehicle]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };

  const handleComponentChange = (component, field, value) => {
    setComponentData(prev => ({
      ...prev,
      [component]: {
        ...prev[component],
        [field]: value
      }
    }));
  };

  // Calculate mileage since last maintenance for components with maintenance dates
  const calculateMileageSinceService = () => {
    // Use formData.currentMileage if set, otherwise default to vehicle's current mileage
    const currentMileage = formData.currentMileage || vehicle?.mileage || 0;
    const oldMileage = vehicle?.mileage || 0;
    
    // Current Mileage - Old Mileage = Mileage Since Service
    const mileageSinceService = Math.max(0, currentMileage - oldMileage);
    
    console.log('🔧 Mileage calculation:', {
      currentMileage,
      oldMileage,
      mileageSinceService
    });
    
    return mileageSinceService;
  };

  // Auto-calculate mileage since service when current mileage changes
  useEffect(() => {
    const calculatedMileage = calculateMileageSinceService();
    
    // Update all components with the same calculated mileage since service
    setComponentData(prev => ({
      suspension: { ...prev.suspension, mileageSinceService: calculatedMileage },
      brakes: { ...prev.brakes, mileageSinceService: calculatedMileage },
      engine: { ...prev.engine, mileageSinceService: calculatedMileage },
      transmission: { ...prev.transmission, mileageSinceService: calculatedMileage },
    }));
  }, [formData.currentMileage, vehicle?.mileage]);



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
      
      const maintenanceData = {
        vehicleId: vehicle.id,
        date: new Date().toISOString().split('T')[0], // Use current date as overall maintenance date
        technicianId: formData.technicianId,
        generalNotes: formData.generalNotes,
        components: {
          suspension: componentData.suspension,
          brakes: componentData.brakes,
          engine: componentData.engine,
          transmission: componentData.transmission,
        },
        // Update vehicle mileage to the current mileage entered by user
        newMileage: formData.currentMileage,
      };

      console.log('🔧 Sending maintenance data:', maintenanceData);

      const response = await fetch(`${apiUrl}/api/fleet/maintenance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5',
        },
        body: JSON.stringify(maintenanceData),
      });

      if (!response.ok) {
        throw new Error('Failed to add maintenance record');
      }

      const result = await response.json();
      console.log('✅ Maintenance record added:', result.maintenanceRecord);
      onMaintenanceAdded(result.maintenanceRecord);
      onClose();
    } catch (error) {
      console.error('Error adding maintenance record:', error);
      setError('Failed to add maintenance record. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`${themeClasses.primary} border ${themeClasses.border} rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${themeClasses.border}`}>
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-2 rounded-lg">
              <Wrench size={20} className="text-white" />
            </div>
            <h2 className={`text-xl font-bold ${themeClasses.textPrimary}`}>Add Maintenance Record - {vehicle?.model}</h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${themeClasses.textMuted} hover:${themeClasses.textPrimary} ${themeClasses.buttonSecondary} transition-colors`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400" />
              <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-4`}>
            <h3 className={`text-lg font-bold ${themeClasses.textPrimary} mb-4`}>Maintenance Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Technician */}
              <div>
                <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                  Technician ID/Name *
                </label>
                <input
                  type="text"
                  value={formData.technicianId}
                  onChange={(e) => handleInputChange('technicianId', e.target.value)}
                  placeholder="Enter technician ID or name"
                  className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.borderFocus} focus:outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                  required
                />
              </div>

              {/* Current Vehicle Mileage */}
              <div>
                <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                  Current Vehicle Mileage *
                </label>
                <input
                  type="number"
                  value={formData.currentMileage}
                  onChange={(e) => handleInputChange('currentMileage', parseInt(e.target.value) || 0)}
                  placeholder="Enter current mileage"
                  min="0"
                  className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.borderFocus} focus:outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                  required
                />
              </div>
            </div>

            {/* General Notes */}
            <div className="mt-4">
              <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                General Maintenance Notes
              </label>
              <textarea
                value={formData.generalNotes}
                onChange={(e) => handleInputChange('generalNotes', e.target.value)}
                placeholder="Enter general notes about this maintenance session..."
                rows="3"
                className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.borderFocus} focus:outline-none resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              />
            </div>
          </div>

          {/* Component Details */}
          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-4`}>
            <h3 className={`text-lg font-bold ${themeClasses.textPrimary} mb-4`}>Component Details</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Suspension Section */}
              <div className={`${themeClasses.card} border ${themeClasses.border} rounded-lg p-4`}>
                <div className="flex items-center gap-2 mb-4">
                  <Activity size={20} className="text-green-400" />
                  <h4 className={`${themeClasses.textPrimary} font-semibold text-lg`}>Suspension</h4>
                </div>
                
                <div className="space-y-3">
                  {/* Health Percentage */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm mb-2 block`}>
                      Health Percentage: {componentData.suspension.health}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={componentData.suspension.health}
                      onChange={(e) => handleComponentChange('suspension', 'health', parseInt(e.target.value))}
                      className="w-full accent-green-500 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  
                  {/* Maintenance Date */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Maintenance Date</label>
                    <input
                      type="date"
                      value={componentData.suspension.maintenanceDate}
                      onChange={(e) => handleComponentChange('suspension', 'maintenanceDate', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                    />
                  </div>
                  
                  {/* Mileage Since Service (Auto-calculated) */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Mileage Since Service (Auto-calculated)</label>
                    <input
                      type="text"
                      value={componentData.suspension.mileageSinceService.toLocaleString() + ' mi'}
                      disabled
                      className={`w-full px-3 py-2 border ${themeClasses.borderInput} rounded text-sm mt-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 cursor-not-allowed font-medium`}
                      min="0"
                    />
                  </div>
                  

                  
                  {/* Condition Notes */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Condition Notes</label>
                    <textarea
                      value={componentData.suspension.conditionNotes}
                      onChange={(e) => handleComponentChange('suspension', 'conditionNotes', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                      rows="2"
                      placeholder="Suspension condition notes..."
                    />
                  </div>
                </div>
              </div>

              {/* Brakes Section */}
              <div className={`${themeClasses.card} border ${themeClasses.border} rounded-lg p-4`}>
                <div className="flex items-center gap-2 mb-4">
                  <Shield size={20} className="text-red-400" />
                  <h4 className={`${themeClasses.textPrimary} font-semibold text-lg`}>Brakes</h4>
                </div>
                
                <div className="space-y-3">
                  {/* Health Percentage */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm mb-2 block`}>
                      Health Percentage: {componentData.brakes.health}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={componentData.brakes.health}
                      onChange={(e) => handleComponentChange('brakes', 'health', parseInt(e.target.value))}
                      className="w-full accent-red-500 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  
                  {/* Maintenance Date */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Maintenance Date</label>
                    <input
                      type="date"
                      value={componentData.brakes.maintenanceDate}
                      onChange={(e) => handleComponentChange('brakes', 'maintenanceDate', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                    />
                  </div>
                  
                  {/* Mileage Since Service (Auto-calculated) */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Mileage Since Service (Auto-calculated)</label>
                    <input
                      type="text"
                      value={componentData.brakes.mileageSinceService.toLocaleString() + ' mi'}
                      disabled
                      className={`w-full px-3 py-2 border ${themeClasses.borderInput} rounded text-sm mt-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 cursor-not-allowed font-medium`}
                      min="0"
                    />
                  </div>
                  
                  {/* Pad Thickness */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Pad Thickness</label>
                    <input
                      type="text"
                      value={componentData.brakes.padThickness}
                      onChange={(e) => handleComponentChange('brakes', 'padThickness', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                      placeholder="e.g., 8mm"
                    />
                  </div>
                  
                  {/* Fluid Level */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Fluid Level</label>
                    <select
                      value={componentData.brakes.fluidLevel}
                      onChange={(e) => handleComponentChange('brakes', 'fluidLevel', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                    >
                      {fluidLevelOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Condition Notes */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Condition Notes</label>
                    <textarea
                      value={componentData.brakes.conditionNotes}
                      onChange={(e) => handleComponentChange('brakes', 'conditionNotes', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                      rows="2"
                      placeholder="Brakes condition notes..."
                    />
                  </div>
                </div>
              </div>

              {/* Engine Section */}
              <div className={`${themeClasses.card} border ${themeClasses.border} rounded-lg p-4`}>
                <div className="flex items-center gap-2 mb-4">
                  <Cog size={20} className="text-blue-400" />
                  <h4 className={`${themeClasses.textPrimary} font-semibold text-lg`}>Engine</h4>
                </div>
                
                <div className="space-y-3">
                  {/* Health Percentage */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm mb-2 block`}>
                      Health Percentage: {componentData.engine.health}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={componentData.engine.health}
                      onChange={(e) => handleComponentChange('engine', 'health', parseInt(e.target.value))}
                      className="w-full accent-blue-500 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  
                  {/* Maintenance Date */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Maintenance Date</label>
                    <input
                      type="date"
                      value={componentData.engine.maintenanceDate}
                      onChange={(e) => handleComponentChange('engine', 'maintenanceDate', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                    />
                  </div>
                  
                  {/* Mileage Since Service (Auto-calculated) */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Mileage Since Service (Auto-calculated)</label>
                    <input
                      type="text"
                      value={componentData.engine.mileageSinceService.toLocaleString() + ' mi'}
                      disabled
                      className={`w-full px-3 py-2 border ${themeClasses.borderInput} rounded text-sm mt-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 cursor-not-allowed font-medium`}
                      min="0"
                    />
                  </div>
                  
                  {/* Oil Quality */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Oil Quality</label>
                    <select
                      value={componentData.engine.oilQuality}
                      onChange={(e) => handleComponentChange('engine', 'oilQuality', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                    >
                      {oilQualityOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Condition Notes */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Condition Notes</label>
                    <textarea
                      value={componentData.engine.conditionNotes}
                      onChange={(e) => handleComponentChange('engine', 'conditionNotes', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                      rows="3"
                      placeholder="Engine condition notes..."
                    />
                  </div>
                </div>
              </div>

              {/* Transmission Section */}
              <div className={`${themeClasses.card} border ${themeClasses.border} rounded-lg p-4`}>
                <div className="flex items-center gap-2 mb-4">
                  <Droplet size={20} className="text-purple-400" />
                  <h4 className={`${themeClasses.textPrimary} font-semibold text-lg`}>Transmission</h4>
                </div>
                
                <div className="space-y-3">
                  {/* Health Percentage */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm mb-2 block`}>
                      Health Percentage: {componentData.transmission.health}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={componentData.transmission.health}
                      onChange={(e) => handleComponentChange('transmission', 'health', parseInt(e.target.value))}
                      className="w-full accent-purple-500 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  
                  {/* Maintenance Date */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Maintenance Date</label>
                    <input
                      type="date"
                      value={componentData.transmission.maintenanceDate}
                      onChange={(e) => handleComponentChange('transmission', 'maintenanceDate', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                    />
                  </div>
                  
                  {/* Mileage Since Service (Auto-calculated) */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Mileage Since Service (Auto-calculated)</label>
                    <input
                      type="text"
                      value={componentData.transmission.mileageSinceService.toLocaleString() + ' mi'}
                      disabled
                      className={`w-full px-3 py-2 border ${themeClasses.borderInput} rounded text-sm mt-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 cursor-not-allowed font-medium`}
                      min="0"
                    />
                  </div>
                  
                  {/* Shift Quality */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Shift Quality</label>
                    <select
                      value={componentData.transmission.shiftQuality}
                      onChange={(e) => handleComponentChange('transmission', 'shiftQuality', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                    >
                      {shiftQualityOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Condition Notes */}
                  <div>
                    <label className={`${themeClasses.textMuted} text-sm`}>Condition Notes</label>
                    <textarea
                      value={componentData.transmission.conditionNotes}
                      onChange={(e) => handleComponentChange('transmission', 'conditionNotes', e.target.value)}
                      className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded text-sm mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                      rows="3"
                      placeholder="Transmission condition notes..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className={`flex gap-3 pt-4 border-t ${themeClasses.border}`}>
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-6 py-3 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Adding...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Add Maintenance Record
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMaintenanceModal;
